/**
 * MonthGrid — the month-calendar view. The pager is the timeframe here
 * (CalendarTab passes filteredEntries WITHOUT the timeframe filter).
 *
 * Two layers per week row, mirroring the creator calendar (cg-client-pages
 * MonthCalendar.tsx) so both surfaces read the same:
 *  - Tentpole bands: active tentpole windows as lane-packed purple segments
 *    spanning columns (monthLanes.ts), clipped at week edges with squared
 *    corners where the window continues. The purple is the label (the legend
 *    names it; screen readers get "Tentpole" in the band's name). Click opens
 *    a detail modal with Show plans / + Add plan / Edit tentpole.
 *  - Day chips: entries whose plannedDate falls inside the month (ranged
 *    chips get a trailing →; week-precision renders on its plannedDate).
 *
 * "No specific day" strip: month-precision entries of this month, plus
 * carry-over plans that started earlier but are still running (⟶-prefixed),
 * judged by their effective end (a week plan runs six days past its start)
 * — a range that began last month would otherwise vanish.
 * Chips cap at 3/cell; "+N more" and any cell with chips or tentpole coverage
 * open a day-detail modal: that day's plans first (what the day is about),
 * then the tentpoles running through it as chips.
 */

import React, { useState } from 'react';
import { Button, Card, Modal } from '../../ui';
import WidgetHeader from '../WidgetHeader';
import EntryRow from './EntryRow';
import TimingPill from './TimingPill';
import {
  CalendarEntry,
  CalendarEntryStatus,
  ENTRY_STATUSES,
  ENTRY_STATUS_LABELS,
  Tentpole,
  TENTPOLE_CATEGORY_LABELS,
} from './types';
import {
  addMonths,
  calendarEntryEffectiveEndYmd,
  ENTRY_STATUS_CHIP_COLORS,
  entryDisplayParts,
  fmtDate,
  fmtTentpoleWindow,
  monthKeyOf,
  monthLabel,
  plansWord,
  relativeDayLabel,
  tentpoleLabel,
  todayYmd,
  weekdayLong,
} from './shared';
import { assignTentpoleLanes, monthEndYmd, splitMonthWeeks, tentpolesForRange, tentpolesOnDay } from './monthLanes';

interface MonthGridProps {
  /** 'YYYY-MM' — owned by CalendarTab so it survives view toggles. */
  monthKey: string;
  onMonthChange: (next: string) => void;
  /** filteredEntries — timeframe filter NOT applied (the pager governs). */
  entries: CalendarEntry[];
  tentpoleBySlug: Map<string, Tentpole>;
  onOpenEntry: (id: string) => void;
  /** Moment/category-filtered catalog; active + month-overlap applied here. */
  tentpoles: Tentpole[];
  /** Gates Edit tentpole and + Add plan in the tentpole detail modal. */
  canManage: boolean;
  onEditTentpole: (tentpole: Tentpole) => void;
  onAddEntryFor: (tentpoleSlug: string) => void;
  /** "Show plans" — the tentpole's plans in the By tentpole view. */
  onShowTentpolePlans: (tentpoleSlug: string) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_CHIPS = 3;

// Band geometry — the per-cell spacer and the absolute overlay must agree,
// or bands cover chips. CELL_PADDING and DAY_NUMBER_ROW_HEIGHT feed both
// cellStyle / the day-number row and the overlay's top offset, so a padding
// change moves them together; the spacer reserves laneCount * LANE_STRIDE
// below the number row.
const CELL_PADDING = 6;
const DAY_NUMBER_ROW_HEIGHT = 18;
const LANE_HEIGHT = 21;
const LANE_GAP = 3;
const LANE_STRIDE = LANE_HEIGHT + LANE_GAP;
const SEGMENT_TOP = CELL_PADDING + DAY_NUMBER_ROW_HEIGHT + 2;

const MonthGrid: React.FC<MonthGridProps> = ({
  monthKey,
  onMonthChange,
  entries,
  tentpoleBySlug,
  onOpenEntry,
  tentpoles,
  canManage,
  onEditTentpole,
  onAddEntryFor,
  onShowTentpolePlans,
}) => {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedTentpoleSlug, setSelectedTentpoleSlug] = useState<string | null>(null);

  const weeks = splitMonthWeeks(monthKey);
  const monthStart = `${monthKey}-01`;
  const today = todayYmd();

  // Active tentpoles overlapping the month itself — same month-scoped range
  // the creator calendar fetches, so a window living entirely in an adjacent
  // month's pad days appears on neither surface (nor in the subtitle count).
  // Bands still span pad days: lane packing clips per week below.
  const monthTentpoles = tentpolesForRange(tentpoles, monthStart, monthEndYmd(monthKey));

  // Single pass: day chips keyed by plannedDate; strip = month-precision
  // entries of this month + carry-over ranges from earlier months.
  const byDay: Record<string, CalendarEntry[]> = {};
  const stripEntries: CalendarEntry[] = [];
  let inMonthCount = 0;
  entries.forEach((e) => {
    if (monthKeyOf(e.plannedDate) === monthKey) {
      if (e.datePrecision === 'month') stripEntries.push(e);
      else (byDay[e.plannedDate] = byDay[e.plannedDate] || []).push(e);
      inMonthCount += 1;
    } else if (e.plannedDate < monthStart && calendarEntryEffectiveEndYmd(e) >= monthStart) {
      // Carry-over still running this month: a range, or a week plan whose
      // week (no end date stored) crosses into it.
      stripEntries.push(e);
      inMonthCount += 1;
    }
  });
  // Chips and the day modal display channel-first, so they sort channel-first
  // too — an invisible sort key reads as an unsorted list.
  Object.keys(byDay).forEach((day) => {
    byDay[day].sort(
      (a, b) =>
        entryDisplayParts(a).primary.localeCompare(entryDisplayParts(b).primary) ||
        a.creatorLabel.localeCompare(b.creatorLabel),
    );
  });
  stripEntries.sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));

  // Chips lead with the targeted channel; the authoring page survives in the
  // tooltip so agency plans stay attributed.
  const chip = (e: CalendarEntry, carryOver?: boolean) => {
    const { primary, byline } = entryDisplayParts(e);
    return (
      <button
        key={e.id}
        type="button"
        style={chipStyle(e.status)}
        title={`${primary}${byline ? ` (by ${byline})` : ''} — ${e.title}`}
        onClick={(ev) => { ev.stopPropagation(); onOpenEntry(e.id); }}
      >
        {carryOver ? `⟶ ${primary}` : `${primary}${e.plannedEndDate ? ' →' : ''}`}
      </button>
    );
  };

  const openTentpole = (t: Tentpole) => {
    setSelectedDay(null);
    setSelectedTentpoleSlug(t.slug);
  };

  const dayEntries = selectedDay ? byDay[selectedDay] || [] : [];
  const dayTentpoles = selectedDay ? tentpolesOnDay(monthTentpoles, selectedDay) : [];
  // Re-derived from live props (like selectedDay → dayEntries) so the open
  // modal — and the object handed to Edit — tracks the dashboard's periodic
  // tentpole refresh instead of freezing a click-time snapshot.
  const selectedTentpole = selectedTentpoleSlug
    ? monthTentpoles.find((t) => t.slug === selectedTentpoleSlug) ?? null
    : null;
  // Every loaded plan of the tentpole under the current filters, not only this
  // month's — the same set "Show plans" lands on.
  const selectedPlanCount = selectedTentpole
    ? entries.filter((e) => e.tentpoleSlug === selectedTentpole.slug).length
    : 0;
  const closeTentpoleThen = (action: (t: Tentpole) => void) => {
    if (!selectedTentpole) return;
    const t = selectedTentpole;
    setSelectedTentpoleSlug(null);
    action(t);
  };

  return (
    <Card padding={16}>
      <WidgetHeader
        title={monthLabel(monthKey)}
        subtitle={
          plansWord(inMonthCount) +
          (monthTentpoles.length > 0
            ? ` · ${monthTentpoles.length} ${monthTentpoles.length === 1 ? 'tentpole' : 'tentpoles'}`
            : '')
        }
        right={
          <>
            <Button size="sm" variant="ghost" onClick={() => onMonthChange(addMonths(monthKey, -1))} aria-label="Previous month">‹</Button>
            <Button size="sm" variant="subtle" onClick={() => onMonthChange(monthKeyOf(todayYmd()))}>Today</Button>
            <Button size="sm" variant="ghost" onClick={() => onMonthChange(addMonths(monthKey, 1))} aria-label="Next month">›</Button>
          </>
        }
      />
      {stripEntries.length > 0 && (
        <div style={stripStyle}>
          <span style={stripLabelStyle}>No specific day · {stripEntries.length}</span>
          {stripEntries.map((e) => chip(e, monthKeyOf(e.plannedDate) !== monthKey))}
        </div>
      )}
      <div style={weekdayHeaderStyle}>
        {WEEKDAYS.map((w) => <div key={w} style={weekdayCellStyle}>{w}</div>)}
      </div>
      <div style={monthShellStyle}>
        {weeks.map((week) => {
          const { segments, laneCount } = assignTentpoleLanes(monthTentpoles, week);
          return (
            <div key={week[0]} style={weekRowStyle}>
              <div style={weekGridStyle}>
                {week.map((ymd) => {
                  if (monthKeyOf(ymd) !== monthKey) {
                    return <div key={ymd} style={padCellStyle} />;
                  }
                  const dayList = byDay[ymd] || [];
                  const isToday = ymd === today;
                  const overflow = dayList.length - MAX_CHIPS;
                  // Tentpole-covered days open the day modal too — its
                  // tentpole rows are the only day-scoped view of a window.
                  const clickable = dayList.length > 0 || tentpolesOnDay(monthTentpoles, ymd).length > 0;
                  return (
                    <div
                      key={ymd}
                      style={{ ...cellStyle, cursor: clickable ? 'pointer' : 'default' }}
                      onClick={clickable ? () => setSelectedDay(ymd) : undefined}
                    >
                      <div style={{ display: 'flex', height: DAY_NUMBER_ROW_HEIGHT, alignItems: 'flex-start' }}>
                        <span style={isToday ? todayNumberStyle : dayNumberStyle}>
                          {parseInt(ymd.slice(8, 10), 10)}
                        </span>
                      </div>
                      {laneCount > 0 && <div aria-hidden style={{ height: laneCount * LANE_STRIDE, flex: 'none' }} />}
                      {dayList.slice(0, MAX_CHIPS).map((e) => chip(e))}
                      {overflow > 0 && (
                        <button
                          type="button"
                          style={moreStyle}
                          onClick={(ev) => { ev.stopPropagation(); setSelectedDay(ymd); }}
                        >
                          +{overflow} more
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {laneCount > 0 && (
                <div
                  style={{
                    ...segmentLayerStyle,
                    gridTemplateRows: `repeat(${laneCount}, ${LANE_HEIGHT}px)`,
                  }}
                >
                  {segments.map(({ tentpole: t, startCol, endCol, lane, continuesLeft, continuesRight }) => (
                    <button
                      key={t.slug}
                      type="button"
                      style={segmentStyle(startCol, endCol, lane, continuesLeft, continuesRight)}
                      onClick={() => openTentpole(t)}
                      title={`${tentpoleLabel(t)} · ${fmtTentpoleWindow(t)}`}
                      aria-label={`${tentpoleLabel(t)}, Tentpole, ${fmtTentpoleWindow(t)}`}
                    >
                      <span style={segmentTitleStyle}>{tentpoleLabel(t)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={legendStyle} aria-label="Calendar legend">
        <span style={legendItemStyle}>
          <i style={{ ...legendSwatchStyle, background: 'var(--calendar-tentpole-soft)', border: '1px solid var(--calendar-tentpole-border)' }} />
          Tentpole
        </span>
        {ENTRY_STATUSES.map((status) => (
          <span key={status} style={legendItemStyle}>
            <i style={{ ...legendSwatchStyle, background: ENTRY_STATUS_CHIP_COLORS[status].bg }} />
            {ENTRY_STATUS_LABELS[status]}
          </span>
        ))}
      </div>
      {inMonthCount === 0 && (
        <div style={emptyHintStyle}>
          {monthTentpoles.length > 0
            ? `No creator plans in ${monthLabel(monthKey)} yet — tentpole windows are shown on the grid.`
            : `Nothing scheduled in ${monthLabel(monthKey)} among the plans loaded so far.`}
        </div>
      )}
      {selectedDay && (
        <Modal
          open
          onClose={() => setSelectedDay(null)}
          width={560}
          variant="compact"
          eyebrow={relativeDayLabel(selectedDay) ?? 'Calendar day'}
          title={`${weekdayLong(selectedDay)}, ${fmtDate(selectedDay)}`}
        >
          {dayEntries.length > 0 ? (
            dayEntries.map((e) => (
              <EntryRow
                key={e.id}
                entry={e}
                tentpole={e.tentpoleSlug ? tentpoleBySlug.get(e.tentpoleSlug) : undefined}
                showMoment
                compact
                onOpen={(id) => { setSelectedDay(null); onOpenEntry(id); }}
              />
            ))
          ) : (
            <div style={dayEmptyStyle}>No plans on this day yet.</div>
          )}
          {dayTentpoles.length > 0 && (
            <div style={daySectionStyle}>
              <div style={microLabelStyle}>{dayEntries.length > 0 ? 'Also happening' : 'Tentpoles on this day'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {dayTentpoles.map((t) => (
                  <button
                    key={t.slug}
                    type="button"
                    style={dayTentpoleChipStyle}
                    title={fmtTentpoleWindow(t)}
                    onClick={() => openTentpole(t)}
                  >
                    {tentpoleLabel(t)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
      {selectedTentpole && (
        <Modal
          open
          onClose={() => setSelectedTentpoleSlug(null)}
          width={480}
          variant="compact"
          eyebrow={`Tentpole · ${TENTPOLE_CATEGORY_LABELS[selectedTentpole.category]}`}
          title={tentpoleLabel(selectedTentpole)}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {canManage && (
                  <Button size="sm" variant="ghost" onClick={() => closeTentpoleThen(onEditTentpole)}>
                    Edit tentpole
                  </Button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {canManage && (
                  <Button size="sm" variant="subtle" onClick={() => closeTentpoleThen((t) => onAddEntryFor(t.slug))}>
                    + Add plan
                  </Button>
                )}
                <Button size="sm" variant="primary" onClick={() => closeTentpoleThen((t) => onShowTentpolePlans(t.slug))}>
                  Show plans
                </Button>
              </div>
            </div>
          }
        >
          <div style={tentpoleMetaStyle}>
            <TimingPill tentpole={selectedTentpole} />
            <span style={{ color: 'var(--fg-2)', fontWeight: 600 }}>{fmtTentpoleWindow(selectedTentpole)}</span>
            <span aria-hidden="true">·</span>
            <span>{plansWord(selectedPlanCount)}</span>
          </div>
          {selectedTentpole.description && (
            <p style={tentpoleDescriptionStyle}>{selectedTentpole.description}</p>
          )}
        </Modal>
      )}
    </Card>
  );
};

const chipStyle = (status: CalendarEntryStatus): React.CSSProperties => ({
  fontSize: 10.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  padding: '2px 6px',
  borderRadius: 'var(--radius-sm)',
  background: ENTRY_STATUS_CHIP_COLORS[status].bg,
  color: ENTRY_STATUS_CHIP_COLORS[status].fg,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  textAlign: 'left',
  border: 'none',
  cursor: 'pointer',
  display: 'block',
});

const stripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
  padding: '10px 0 12px',
};

const stripLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  fontFamily: 'var(--font-sans)',
  marginRight: 4,
};

const weekdayHeaderStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  marginTop: 12,
};

const weekdayCellStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  fontFamily: 'var(--font-sans)',
  padding: '0 6px 6px',
};

/** The gap-as-gridline trick lives here: week rows are transparent, so the
 *  1px flex gap and each week's 1px column gap expose this background. */
const monthShellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  background: 'var(--border-subtle)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

const weekRowStyle: React.CSSProperties = {
  position: 'relative',
};

const weekGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 1,
};

/** Band overlay — columnGap 1 keeps its columns pixel-aligned with the
 *  gap:1 day grid underneath. Clicks pass through except on the bands. */
const segmentLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: SEGMENT_TOP,
  left: 0,
  right: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  columnGap: 1,
  rowGap: LANE_GAP,
  pointerEvents: 'none',
};

const segmentStyle = (
  startCol: number,
  endCol: number,
  lane: number,
  continuesLeft: boolean,
  continuesRight: boolean,
): React.CSSProperties => ({
  gridColumn: `${startCol + 1} / ${endCol + 2}`,
  gridRow: lane + 1,
  height: LANE_HEIGHT,
  marginLeft: continuesLeft ? 0 : 3,
  marginRight: continuesRight ? 0 : 3,
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  minWidth: 0,
  padding: '0 6px',
  background: 'var(--calendar-tentpole-soft)',
  border: '1px solid var(--calendar-tentpole-border)',
  borderTopLeftRadius: continuesLeft ? 2 : 6,
  borderBottomLeftRadius: continuesLeft ? 2 : 6,
  borderTopRightRadius: continuesRight ? 2 : 6,
  borderBottomRightRadius: continuesRight ? 2 : 6,
  color: 'var(--calendar-tentpole)',
  fontFamily: 'var(--font-sans)',
  cursor: 'pointer',
  pointerEvents: 'auto',
  overflow: 'hidden',
});

const segmentTitleStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const microLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  fontFamily: 'var(--font-sans)',
  marginBottom: 8,
};

const dayEmptyStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
  padding: '4px 0',
};

const daySectionStyle: React.CSSProperties = {
  marginTop: 14,
  paddingTop: 12,
  borderTop: '1px solid var(--border-subtle)',
};

/** Same shape as the rows' moment chip, clickable. */
const dayTentpoleChipStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  color: 'var(--calendar-tentpole)',
  background: 'var(--calendar-tentpole-soft)',
  border: '1px solid var(--calendar-tentpole-border)',
  borderRadius: 'var(--radius-pill)',
  cursor: 'pointer',
};

const tentpoleMetaStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
  color: 'var(--fg-3)',
};

const tentpoleDescriptionStyle: React.CSSProperties = {
  margin: '12px 0 0',
  fontSize: 13,
  lineHeight: 1.55,
  fontFamily: 'var(--font-sans)',
  color: 'var(--fg-2)',
};

const cellStyle: React.CSSProperties = {
  background: 'var(--bg-surface-1)',
  minHeight: 96,
  padding: CELL_PADDING,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 0,
};

const padCellStyle: React.CSSProperties = {
  background: 'var(--cg-neutral-50)',
  minHeight: 96,
};

const dayNumberStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
};

const todayNumberStyle: React.CSSProperties = {
  ...dayNumberStyle,
  background: 'var(--accent)',
  color: 'var(--fg-on-accent)',
  borderRadius: 'var(--radius-pill)',
  padding: '1px 6px',
};

const moreStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  fontFamily: 'var(--font-sans)',
  color: 'var(--fg-link)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  padding: '1px 0',
};

const legendStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 14,
  padding: '10px 2px 0',
};

const legendItemStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
};

const legendSwatchStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: 3,
  display: 'inline-block',
};

const emptyHintStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-3)',
  fontFamily: 'var(--font-sans)',
  padding: '12px 2px 0',
};

export default MonthGrid;
